// src/components/CartSignInUp.js
import { AlertCircle, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from "../utils/axiosHandler";
import AddressAutocomplete from './AddressAutocomplete';
import { Alert, AlertDescription } from './ui/alert';


const CartSignInUp = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  // Step state controls which form is displayed
  // 1: Initial identifier input (email/phone)
  // 2: Login form (password) for existing users
  // 3: Verification code (for phone login or registration)
  // 4: New user registration - additional details (email if phone was provided)
  // 5: New user registration - password
  // 6: New user registration - name
  // 7: New user registration - address (optional)
  const [step, setStep] = useState(1);
  
  // Form data
  const [identifier, setIdentifier] = useState('');
  const [identifierType, setIdentifierType] = useState(''); // 'email' or 'phone'
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [company, setCompany] = useState('');
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [userExists, setUserExists] = useState(false);

  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);

  // Clear form when modal is opened/closed
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setIdentifier('');
      setIdentifierType('');
      setOtpCode('');
      setOtpSent(false);
      setError('');
    }
  }, [isOpen]);

  const checkIdentifierExists = async (type, value) => {
    try {
      setError('');
      
      // Skip empty values
      if (!value.trim()) return false;
      
      const { data } = await axios.post('/api/check-identifier', {
        identifier: value.trim()
      });
      
      if (data.exists) {
        if (type === 'email') {
          setError('This email address is already registered. Please use a different email or sign in.');
        } else if (type === 'phone') {
          setError('This phone number is already registered. Please use a different number or sign in.');
        }
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('Error checking identifier:', err);
      return false;
    }
  };

  // Determine if the input is an email or phone number
  const determineIdentifierType = (value) => {
    // Simple regex for email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(value)) {
      return 'email';
    }
    
    // Simple regex for phone validation (numbers, possibly with +, -, or spaces)
    const phoneRegex = /^[+\d\s-]{8,}$/;
    if (phoneRegex.test(value)) {
      return 'phone';
    }
    
    return '';
  };

  const handleIdentifierChange = (e) => {
    const value = e.target.value;
    setIdentifier(value);
    setIdentifierType(determineIdentifierType(value));
  };

  const sanitizeInput = (input) => {
    return input.trim().replace(/\s+/g, ' ');
  };

  const handleIdentifierSubmit = async () => {
    setError('');
    setLoading(true);
    
    // Validate identifier
    const type = determineIdentifierType(identifier);
    if (!type) {
      setError('Please enter a valid email address or phone number');
      setLoading(false);
      return;
    }
    
    try {
      // Check if user exists with this identifier
      const response = await axios.post('/api/check-identifier', {
        identifier: sanitizeInput(identifier)
      });
      
      setUserExists(response.data.exists);
      
      if (response.data.exists) {
        // User exists, send verification code using main login endpoint
        if (type === 'email') {
          setEmail(identifier);
          // Send email verification code using main login endpoint
          await axios.post('/api/login', {
            identifier: sanitizeInput(identifier)
          });
          setStep(3); // Go directly to verification step
        } else {
          // For phone, send OTP verification using main login endpoint
          setPhone(identifier);
          await axios.post('/api/login', {
            identifier: sanitizeInput(identifier)
          });
          setStep(3);
        }
      } else {
        // User doesn't exist, start registration process
        if (type === 'email') {
          setEmail(identifier);
          setStep(4); // Ask for phone number
        } else {
          setPhone(identifier);
          await sendOtp(identifier);
          setStep(3); // Verify phone number first
        }
      }
    } catch (error) {
      console.error('Error checking identifier:', error);
      setError(error.response?.data?.error || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async (phoneNumber) => {
    try {
      await axios.post('/api/verify-phone', { phone: phoneNumber });
      setOtpSent(true);
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send verification code');
      return false;
    }
  };

  const sendEmailOtp = async (emailAddress) => {
    try {
      await axios.post('/api/verify-email', { email: emailAddress });
      setOtpSent(true);
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send verification code');
      return false;
    }
  };

  const verifyOtp = async () => {
    try {
      console.log('Verifying OTP:', { identifier: identifier, code: otpCode, userExists });
      
      if (userExists) {
        // For existing users, use the main login verification endpoint
        const identifierType = determineIdentifierType(identifier);
        
        if (identifierType === 'email') {
          // Use the main login verification endpoint for email
          const { data } = await axios.post('/api/login/verify', {
            identifier: identifier.trim(),
            verificationCode: otpCode
          });
          
          // Login the user
          await login(data.user, data.token, true);
          await handleCartMerge(data.user.id, data.token);
          
          onClose();
          setTimeout(() => {
            navigate('/cart', { replace: true });
          }, 100);
        } else {
          // Use the main login verification endpoint for phone
          const { data } = await axios.post('/api/login/verify', {
            identifier: identifier.trim(),
            verificationCode: otpCode
          });
          
          // Login the user
          await login(data.user, data.token, true);
          await handleCartMerge(data.user.id, data.token);
          
          onClose();
          setTimeout(() => {
            navigate('/cart', { replace: true });
          }, 100);
        }
      } else {
        // For new users, verify the code first
        const identifierType = determineIdentifierType(identifier);
        let response;
        
        if (identifierType === 'email') {
          // Email verification for registration
          response = await axios.post('/api/verify-email/confirm', { 
            email: identifier.trim(), 
            verificationCode: otpCode 
          });
        } else {
          // Phone verification for registration
          const formattedPhone = phone.trim();
          response = await axios.post('/api/verify-phone/confirm', { 
            phone: formattedPhone, 
            verificationCode: otpCode 
          });
        }
        
        console.log('Verification response:', response.data);
        setStep(4); // Move to registration
      }
      return true;
    } catch (err) {
      console.error('OTP verification error:', err.response?.data || err);
      setError(err.response?.data?.error || 'Invalid verification code. Please try again.');
      return false;
    }
  };



  const handleCartMerge = async (userId, token) => {
    try {
      // Get visitor cart from localStorage
      const visitorCart = JSON.parse(localStorage.getItem('visitorCart') || '[]');
      
      if (visitorCart.length > 0) {
        // Send visitor cart to be merged with user cart
        await axios.post('/api/cart/merge', {
          items: visitorCart
        }, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        // Clear visitor cart after merging
        localStorage.removeItem('visitorCart');
      }
    } catch (error) {
      console.error('Error merging carts:', error);
    }
  };



  const handleRegister = async () => {
    try {
      setLoading(true);
      
      const userData = {
        email,
        password: 'password123', // Use hardcoded password for cart registration
        phone,
        firstName,
        lastName,
        company: company || ''
      };
      
      // Add address if provided
      if (street || city || state || postalCode) {
        userData.addresses = [{
          street: street || '',
          city: city || '',
          state: state || '',
          postalCode: postalCode || '',
          country: 'Canada'
        }];
      }
      
      const { data } = await axios.post('/api/register', userData);
      await login(data.user, data.token, true);
      onClose();
      setTimeout(() => {
        navigate('/cart', { replace: true });
      }, 100);
    } catch (error) {
      setError(error.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    switch (step) {
      case 1:
        handleIdentifierSubmit();
        break;
      case 3:
        await verifyOtp();
        break;
      case 4:
        // Handle phone/email collection and move to next step
        setError('');
        
        // If email was provided initially, check if the phone exists
        if (identifierType === 'email') {
          const phoneExists = await checkIdentifierExists('phone', phone);
          if (phoneExists) return;
          
          // If OTP sent, verify it before proceeding
          if (otpSent) {
            try {
              await axios.post('/api/verify-phone/confirm', { 
                phone: phone.trim(), 
                verificationCode: otpCode 
              });
            } catch (err) {
              setError(err.response?.data?.error || 'Invalid verification code. Please try again.');
              return;
            }
          }
        } else {
          // If phone was provided initially, check if the email exists
          const emailExists = await checkIdentifierExists('email', email);
          if (emailExists) return;
        }
        
        // Skip password step and go directly to name collection
        setStep(6);
        break;
      case 6:
        // Name collection and move to next step
        if (!firstName || !lastName) {
          setError('First and last name are required');
          return;
        }
        setStep(7);
        break;
      case 7:
        // Complete registration
        handleRegister();
        break;
      default:
        break;
    }
  };
  
  if (!isOpen) return null;
  
  const renderStepIndicator = () => {
    if (step === 1 || step === 3) return null; // Don't show for login flow
    
    const totalSteps = 3; // Steps 4, 6, 7 for registration (removed password step)
    const currentRegistrationStep = step === 4 ? 1 : step === 6 ? 2 : step === 7 ? 3 : 0;
    
    return (
      <div className="w-full mb-6">
        <div className="flex justify-between mb-2">
          <span className={` ${currentRegistrationStep >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>Account</span>
          <span className={` ${currentRegistrationStep >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>Personal</span>
          <span className={` ${currentRegistrationStep >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>Address</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
            style={{ width: `${(currentRegistrationStep / totalSteps) * 100}%` }}
          ></div>
        </div>
      </div>
    );
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md  max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 flex justify-between items-center p-4 h-12 mb-4">
          <h2 className="text-xl text-white">{userExists ? 'Sign In' : 'Your Account'}</h2>
          <button 
            onClick={onClose}
            className="p-1 rounded-full text-white hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>

        <div className='p-6'>
        
        {renderStepIndicator()}
        
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <div>
              <p className="text-gray-600 mb-4">
                Please enter your email address or phone number to continue
              </p>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Email or Phone Number
              </label>
              <input
                type="text"
                value={identifier}
                onChange={handleIdentifierChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Email or phone number"
                required
              />
            </div>
          )}
          

          
          {step === 3 && (
            <div>
              <p className="text-gray-600 mb-4">
                {userExists 
                  ? `Please verify your ${identifierType === 'email' ? 'email' : 'phone number'} to log in` 
                  : `Please verify your ${identifierType === 'email' ? 'email' : 'phone number'} to continue`}
              </p>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Verification Code
              </label>
              <input
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-lg tracking-widest"
                placeholder="Enter 6-digit code"
                required
                maxLength={6}
              />
              <div className="mt-2">
                <button 
                  type="button"
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={() => {
                    if (identifierType === 'email') {
                      sendEmailOtp(identifier);
                    } else {
                      sendOtp(phone);
                    }
                  }}
                >
                  Resend code
                </button>
              </div>
            </div>
          )}
          
          {step === 4 && (
            <div>
              <p className="text-gray-600 mb-4">
                Please provide your account details
              </p>
              {identifierType === 'email' ? (
                <>
                  {/* If email was provided, ask for phone */}
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your phone number"
                      required
                    />
                    <button 
                      type="button"
                      className="mt-2 px-3 py-1 bg-blue-500 text-white rounded-md text-sm"
                      onClick={async () => {
                        try {
                          // Validate phone format first
                          const phoneRegex = /^\+?[\d\s-]{8,}$/;
                          if (!phoneRegex.test(phone)) {
                            setError('Please enter a valid phone number');
                            return;
                          }
                          
                          // Check if phone already exists
                          const phoneExists = await checkIdentifierExists('phone', phone);
                          if (phoneExists) {
                            return;
                          }
                          
                          await sendOtp(phone);
                        } catch (err) {
                          setError(err.response?.data?.error || 'Failed to send verification code');
                        }
                      }}
                    >
                      Send Verification Code
                    </button>
                  </div>
                  {otpSent && (
                    <div className="mb-4">
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        Verification Code
                      </label>
                      <input
                        type="text"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter verification code"
                        required
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* If phone was provided, ask for email */}
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your email address"
                      required
                    />
                  </div>
                </>
              )}
            </div>
          )}
          

          
          {step === 6 && (
            <div>
              <p className="text-gray-600 mb-4">
                Please tell us a bit about yourself (Step 2 of 3)
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="First name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Last name"
                    required
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Company (Optional)
                </label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Company name"
                />
              </div>
            </div>
          )}
          
          {step === 7 && (
            <div>
              <p className="text-gray-600 mb-4">
                Add your shipping address (optional - you can add this later) (Step 3 of 3)
              </p>
              <div className="mb-4">
                <AddressAutocomplete 
                  index={0}
                  address={{
                    street: street,
                    city: city,
                    state: state,
                    postalCode: postalCode,
                    country: 'Canada'
                  }}
                  onAddressChange={(index, field, value) => {
                    switch (field) {
                      case 'street':
                        setStreet(value);
                        break;
                      case 'city':
                        setCity(value);
                        break;
                      case 'state':
                        setState(value);
                        break;
                      case 'postalCode':
                        setPostalCode(value);
                        break;
                      default:
                        break;
                    }
                  }}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Province
                  </label>
                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Province"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Postal Code
                </label>
                <input
                  type="text"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Postal code"
                />
              <p className="mt-4 text-sm text-gray-600">
                By completing this registration form, you agree to our <button 
                  type="button" 
                  onClick={() => setShowTermsPopup(true)}
                  className="text-blue-600 hover:text-blue-800 underline font-medium"
                  style={{ 
                    boxShadow: 'none', 
                    border: 'none', 
                    background: 'transparent',
                    padding: 0,
                    margin: 0,
                    display: 'inline'
                  }}
                >Terms & Conditions</button> and <button 
                  type="button" 
                  onClick={() => setShowPrivacyPopup(true)}
                  className="text-blue-600 hover:text-blue-800 underline font-medium"
                  style={{ 
                    boxShadow: 'none', 
                    border: 'none', 
                    background: 'transparent',
                    padding: 0,
                    margin: 0,
                    display: 'inline'
                  }}
                >Privacy Policy</button>.
              </p>
              </div>
            </div>
          )}
          
          <div className="mt-6 flex flex-col sm:flex-row-reverse gap-2">
            <button
              type="submit"
              className="w-full sm:w-auto bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 text-sm font-medium disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Processing...' : step === 1 ? 'Continue' : 
                        step === 3 ? 'Verify' : 
                        step === 7 ? 'Complete Registration' : 'Continue'}
            </button>
            
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Back
              </button>
            )}
          </div>
        </form>
        </div>
      </div>

      {showTermsPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 flex justify-between items-center p-4 h-12">
              <h2 className="text-xl text-white">Terms & Conditions</h2>
              <button 
                onClick={() => setShowTermsPopup(false)}
                className="p-1 rounded-full text-white hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <h3 className="text-lg font-medium mb-4">Terms & Conditions</h3>
              <p className="text-gray-700 mb-4">
                These Terms & Conditions govern your use of our website and services.
              </p>
              <p className="text-gray-700 mb-4">
                By using our services, you agree to comply with these terms.
              </p>
              {/* Add more terms content here */}
              <button
                onClick={() => setShowTermsPopup(false)}
                className="mt-6 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrivacyPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 flex justify-between items-center p-4 h-12">
              <h2 className="text-xl text-white">Privacy Policy</h2>
              <button 
                onClick={() => setShowPrivacyPopup(false)}
                className="p-1 rounded-full text-white hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <h3 className="text-lg font-medium mb-4">Privacy Policy</h3>
              <p className="text-gray-700 mb-4">
                This Privacy Policy explains how we collect, use, and protect your personal information.
              </p>
              <p className="text-gray-700 mb-4">
                We respect your privacy and are committed to protecting your personal data.
              </p>
              {/* Add more privacy policy content here */}
              <button
                onClick={() => setShowPrivacyPopup(false)}
                className="mt-6 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartSignInUp;










// // work well before authocompleadress & email phone exisitng check
// // src/components/CartSignInUp.js
// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { useAuth } from '../context/AuthContext';
// import { Eye, EyeOff, X } from 'lucide-react';
// import { Alert, AlertDescription } from './ui/alert';
// import { AlertCircle } from 'lucide-react';

// const CartSignInUp = ({ isOpen, onClose }) => {
//   const navigate = useNavigate();
//   const { login } = useAuth();
  
//   // Step state controls which form is displayed
//   // 1: Initial identifier input (email/phone)
//   // 2: Login form (password) for existing users
//   // 3: Verification code (for phone login or registration)
//   // 4: New user registration - additional details (email if phone was provided)
//   // 5: New user registration - password
//   // 6: New user registration - name
//   // 7: New user registration - address (optional)
//   const [step, setStep] = useState(1);
  
//   // Form data
//   const [identifier, setIdentifier] = useState('');
//   const [identifierType, setIdentifierType] = useState(''); // 'email' or 'phone'
//   const [password, setPassword] = useState('');
//   const [showPassword, setShowPassword] = useState(false);
//   const [otpCode, setOtpCode] = useState('');
//   const [otpSent, setOtpSent] = useState(false);
//   const [email, setEmail] = useState('');
//   const [phone, setPhone] = useState('');
//   const [firstName, setFirstName] = useState('');
//   const [lastName, setLastName] = useState('');
//   const [street, setStreet] = useState('');
//   const [city, setCity] = useState('');
//   const [state, setState] = useState('');
//   const [postalCode, setPostalCode] = useState('');
//   const [company, setCompany] = useState('');
  
//   const [error, setError] = useState('');
//   const [loading, setLoading] = useState(false);
//   const [userExists, setUserExists] = useState(false);

//   // Clear form when modal is opened/closed
//   useEffect(() => {
//     if (isOpen) {
//       setStep(1);
//       setIdentifier('');
//       setIdentifierType('');
//       setPassword('');
//       setOtpCode('');
//       setOtpSent(false);
//       setError('');
//     }
//   }, [isOpen]);

//   const checkIdentifierExists = async (type, value) => {
//     try {
//       setError('');
      
//       // Skip empty values
//       if (!value.trim()) return false;
      
//       const { data } = await axios.post('/api/check-identifier', {
//         identifier: value.trim()
//       });
      
//       if (data.exists) {
//         if (type === 'email') {
//           setError('This email address is already registered. Please use a different email or sign in.');
//         } else if (type === 'phone') {
//           setError('This phone number is already registered. Please use a different number or sign in.');
//         }
//         return true;
//       }
      
//       return false;
//     } catch (err) {
//       console.error('Error checking identifier:', err);
//       return false;
//     }
//   };

//   // Determine if the input is an email or phone number
//   const determineIdentifierType = (value) => {
//     // Simple regex for email validation
//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     if (emailRegex.test(value)) {
//       return 'email';
//     }
    
//     // Simple regex for phone validation (numbers, possibly with +, -, or spaces)
//     const phoneRegex = /^[+\d\s-]{8,}$/;
//     if (phoneRegex.test(value)) {
//       return 'phone';
//     }
    
//     return '';
//   };

//   const handleIdentifierChange = (e) => {
//     const value = e.target.value;
//     setIdentifier(value);
//     setIdentifierType(determineIdentifierType(value));
//   };

//   const sanitizeInput = (input) => {
//     return input.trim().replace(/\s+/g, ' ');
//   };

//   const handleIdentifierSubmit = async () => {
//     setError('');
//     setLoading(true);
    
//     // Validate identifier
//     const type = determineIdentifierType(identifier);
//     if (!type) {
//       setError('Please enter a valid email address or phone number');
//       setLoading(false);
//       return;
//     }
    
//     try {
//       // Check if user exists with this identifier
//       const response = await axios.post('/api/check-identifier', {
//         identifier: sanitizeInput(identifier)
//       });
      
//       setUserExists(response.data.exists);
      
//       if (response.data.exists) {
//         // User exists, go to login form
//         if (type === 'email') {
//           // For email, proceed to password
//           setEmail(identifier);
//           setStep(2);
//         } else {
//           // For phone, proceed to OTP verification
//           setPhone(identifier);
//           await sendOtp(identifier);
//           setStep(3);
//         }
//       } else {
//         // User doesn't exist, start registration process
//         if (type === 'email') {
//           setEmail(identifier);
//           setStep(4); // Ask for phone number
//         } else {
//           setPhone(identifier);
//           await sendOtp(identifier);
//           setStep(3); // Verify phone number first
//         }
//       }
//     } catch (error) {
//       console.error('Error checking identifier:', error);
//       setError(error.response?.data?.error || 'An error occurred. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const sendOtp = async (phoneNumber) => {
//     try {
//       await axios.post('/api/verify-phone', { phone: phoneNumber });
//       setOtpSent(true);
//       return true;
//     } catch (err) {
//       setError(err.response?.data?.error || 'Failed to send verification code');
//       return false;
//     }
//   };

//   const verifyOtp = async () => {
//     try {
//       // Make sure phone is formatted consistently
//       const formattedPhone = phone.trim();
      
//       console.log('Verifying OTP:', { phone: formattedPhone, code: otpCode });
      
//       if (userExists) {
//         // For existing users, go directly to login without separate verification
//         handlePhoneLogin();
//       } else {
//         // For new users, verify the code first
//         const response = await axios.post('/api/verify-phone/confirm', { 
//           phone: formattedPhone, 
//           verificationCode: otpCode 
//         });
        
//         console.log('Verification response:', response.data);
//         setStep(4); // Move to registration
//       }
//       return true;
//     } catch (err) {
//       console.error('OTP verification error:', err.response?.data || err);
//       setError(err.response?.data?.error || 'Invalid verification code. Please try again.');
//       return false;
//     }
//   };

//   const handlePhoneLogin = async () => {
//     try {
//       setLoading(true);
      
//       console.log('Phone login attempt with:', { 
//         phone, 
//         otpCode,
//         userExists
//       });
      
//       // Important: For existing users with phone numbers,
//       // we need to ensure we've already verified the OTP before trying to log in
      
//       const { data } = await axios.post('/api/cart/login', {
//         identifier: phone,
//         useOtp: true,
//         verificationCode: otpCode
//       });
      
//       await login(data.user, data.token, true);
//       onClose();
      
//       setTimeout(() => {
//         navigate('/cart', { replace: true });
//       }, 100);
//     } catch (error) {
//       console.error('Phone login error:', error.response?.data || error);
//       setError(error.response?.data?.error || 'Login failed. Please check your verification code.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handlePasswordLogin = async () => {
//     try {
//       setLoading(true);
//       const { data } = await axios.post('/api/cart/login', {
//         identifier: email || identifier,
//         password
//       });
      
//       await login(data.user, data.token, true);
//       onClose(); // Close the modal after successful login
//       setTimeout(() => {
//         navigate('/cart', { replace: true });
//       }, 100);
//     } catch (error) {
//       setError(error.response?.data?.error || 'Invalid credentials');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleRegister = async () => {
//     try {
//       setLoading(true);
      
//       const userData = {
//         email,
//         password,
//         phone,
//         firstName,
//         lastName,
//         company: company || ''
//       };
      
//       // Add address if provided
//       if (street || city || state || postalCode) {
//         userData.addresses = [{
//           street: street || '',
//           city: city || '',
//           state: state || '',
//           postalCode: postalCode || '',
//           country: 'Canada'
//         }];
//       }
      
//       const { data } = await axios.post('/api/register', userData);
//       await login(data.user, data.token, true);
//       onClose();
//       setTimeout(() => {
//         navigate('/cart', { replace: true });
//       }, 100);
//     } catch (error) {
//       setError(error.response?.data?.error || 'Registration failed');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setError('');
    
//     switch (step) {
//       case 1:
//         handleIdentifierSubmit();
//         break;
//       case 2:
//         handlePasswordLogin();
//         break;
//       case 3:
//         await verifyOtp();
//         break;
//       case 4:
//         // Handle phone/email collection and move to next step
//         setStep(5);
//         break;
//       case 5:
//         // Password collection and move to next step
//         if (password.length < 8) {
//           setError('Password must be at least 8 characters long');
//           return;
//         }
//         setStep(6);
//         break;
//       case 6:
//         // Name collection and move to next step
//         if (!firstName || !lastName) {
//           setError('First and last name are required');
//           return;
//         }
//         setStep(7);
//         break;
//       case 7:
//         // Complete registration
//         handleRegister();
//         break;
//       default:
//         break;
//     }
//   };
  
//   if (!isOpen) return null;
  
//   const renderStepIndicator = () => {
//     if (step === 1 || step === 2 || step === 3) return null; // Don't show for login flow
    
//     const totalSteps = 4; // Steps 4-7 for registration
//     const currentRegistrationStep = step - 3; // Adjust to get 1-4
    
//     return (
//       <div className="w-full mb-6">
//         <div className="flex justify-between mb-2">
//           <span className={` ${currentRegistrationStep >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>Account</span>
//           <span className={` ${currentRegistrationStep >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>Security</span>
//           <span className={` ${currentRegistrationStep >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>Personal</span>
//           <span className={` ${currentRegistrationStep >= 4 ? 'text-blue-600' : 'text-gray-400'}`}>Address</span>
//         </div>
//         <div className="w-full bg-gray-200 rounded-full h-2.5">
//           <div 
//             className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
//             style={{ width: `${(currentRegistrationStep / totalSteps) * 100}%` }}
//           ></div>
//         </div>
//       </div>
//     );
//   };
  
//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <div className="bg-white rounded-lg shadow-xl w-full max-w-md  max-h-[90vh] overflow-y-auto">
//         <div className="bg-gradient-to-r from-blue-600 to-indigo-600 flex justify-between items-center p-4 h-12 mb-4">
//           <h2 className="text-xl text-white">{userExists ? 'Sign In' : 'Your Account'}</h2>
//           <button 
//             onClick={onClose}
//             className="p-1 rounded-full text-white hover:bg-gray-100"
//           >
//             <X size={20} />
//           </button>
//         </div>

//         <div className='p-6'>
        
//         {renderStepIndicator()}
        
//         {error && (
//           <Alert variant="destructive" className="mb-4">
//             <AlertCircle className="h-4 w-4" />
//             <AlertDescription>{error}</AlertDescription>
//           </Alert>
//         )}
        
//         <form onSubmit={handleSubmit}>
//           {step === 1 && (
//             <div>
//               <p className="text-gray-600 mb-4">
//                 Please enter your email address or phone number to continue
//               </p>
//               <label className="block text-gray-700 text-sm font-medium mb-2">
//                 Email or Phone Number
//               </label>
//               <input
//                 type="text"
//                 value={identifier}
//                 onChange={handleIdentifierChange}
//                 className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 placeholder="Email or phone number"
//                 required
//               />
//             </div>
//           )}
          
//           {step === 2 && (
//             <div>
//               <p className="text-gray-600 mb-4">
//                 Welcome back! Please enter your password
//               </p>
//               <label className="block text-gray-700 text-sm font-medium mb-2">
//                 Password
//               </label>
//               <div className="relative">
//                 <input
//                   type={showPassword ? "text" : "password"}
//                   value={password}
//                   onChange={(e) => setPassword(e.target.value)}
//                   className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   placeholder="Enter your password"
//                   required
//                 />
//                 <button
//                   type="button"
//                   onClick={() => setShowPassword(!showPassword)}
//                   style={{ 
//                     boxShadow: 'none', 
//                     border: 'none', 
//                     background: 'transparent',
//                     transform: 'none',
//                     marginTop: -20+'px',
//                   }}
//                   className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
//                 >
//                   {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
//                 </button>
//               </div>
//               <div className="mt-2 text-right">
//                 <button 
//                   type="button"
//                   className="text-sm text-blue-600 hover:text-blue-800"
//                   style={{ 
//                     boxShadow: 'none', 
//                     border: 'none', 
//                     background: 'transparent',
//                     transform: 'none',
//                   }}
//                   onClick={() => navigate('/forgot-password')}
//                 >
//                   Forgot password?
//                 </button>
//               </div>
//             </div>
//           )}
          
//           {step === 3 && (
//             <div>
//               <p className="text-gray-600 mb-4">
//                 {userExists 
//                   ? 'Please verify your phone number to log in' 
//                   : 'Please verify your phone number to continue'}
//               </p>
//               <label className="block text-gray-700 text-sm font-medium mb-2">
//                 Verification Code
//               </label>
//               <input
//                 type="text"
//                 value={otpCode}
//                 onChange={(e) => setOtpCode(e.target.value)}
//                 className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 placeholder="Enter verification code"
//                 required
//               />
//               <div className="mt-2">
//                 <button 
//                   type="button"
//                   className="text-sm text-blue-600 hover:text-blue-800"
//                   onClick={() => sendOtp(phone)}
//                 >
//                   Resend code
//                 </button>
//               </div>
//             </div>
//           )}
          
//           {step === 4 && (
//             <div>
//               <p className="text-gray-600 mb-4">
//                 Please provide your account details
//               </p>
//               {identifierType === 'email' ? (
//                 <>
//                   {/* If email was provided, ask for phone */}
//                   <div className="mb-4">
//                     <label className="block text-gray-700 text-sm font-medium mb-2">
//                       Phone Number
//                     </label>
//                     <input
//                       type="tel"
//                       value={phone}
//                       onChange={(e) => setPhone(e.target.value)}
//                       className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                       placeholder="Enter your phone number"
//                       required
//                     />
//                     <button 
//                       type="button"
//                       className="mt-2 px-3 py-1 bg-blue-500 text-white rounded-md text-sm"
//                       onClick={() => sendOtp(phone)}
//                     >
//                       Send Verification Code
//                     </button>
//                   </div>
//                   {otpSent && (
//                     <div className="mb-4">
//                       <label className="block text-gray-700 text-sm font-medium mb-2">
//                         Verification Code
//                       </label>
//                       <input
//                         type="text"
//                         value={otpCode}
//                         onChange={(e) => setOtpCode(e.target.value)}
//                         className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Enter verification code"
//                         required
//                       />
//                     </div>
//                   )}
//                 </>
//               ) : (
//                 <>
//                   {/* If phone was provided, ask for email */}
//                   <div className="mb-4">
//                     <label className="block text-gray-700 text-sm font-medium mb-2">
//                       Email Address
//                     </label>
//                     <input
//                       type="email"
//                       value={email}
//                       onChange={(e) => setEmail(e.target.value)}
//                       className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                       placeholder="Enter your email address"
//                       required
//                     />
//                   </div>
//                 </>
//               )}
//             </div>
//           )}
          
//           {step === 5 && (
//             <div>
//               <p className="text-gray-600 mb-4">
//                 Create a secure password
//               </p>
//               <label className="block text-gray-700 text-sm font-medium mb-2">
//                 Password
//               </label>
//               <div className="relative">
//                 <input
//                   type={showPassword ? "text" : "password"}
//                   value={password}
//                   onChange={(e) => setPassword(e.target.value)}
//                   className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   placeholder="Create a password"
//                   required
//                   minLength={8}
//                 />
//                 <button
//                   type="button"
//                   onClick={() => setShowPassword(!showPassword)}
//                   className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
//                 >
//                   {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
//                 </button>
//               </div>
//               <p className="text-xs text-gray-500 mt-1">
//                 Password must be at least 8 characters long
//               </p>
//             </div>
//           )}
          
//           {step === 6 && (
//             <div>
//               <p className="text-gray-600 mb-4">
//                 Please tell us a bit about yourself
//               </p>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-gray-700 text-sm font-medium mb-2">
//                     First Name
//                   </label>
//                   <input
//                     type="text"
//                     value={firstName}
//                     onChange={(e) => setFirstName(e.target.value)}
//                     className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     placeholder="First name"
//                     required
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-gray-700 text-sm font-medium mb-2">
//                     Last Name
//                   </label>
//                   <input
//                     type="text"
//                     value={lastName}
//                     onChange={(e) => setLastName(e.target.value)}
//                     className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     placeholder="Last name"
//                     required
//                   />
//                 </div>
//               </div>
//               <div className="mt-4">
//                 <label className="block text-gray-700 text-sm font-medium mb-2">
//                   Company (Optional)
//                 </label>
//                 <input
//                   type="text"
//                   value={company}
//                   onChange={(e) => setCompany(e.target.value)}
//                   className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   placeholder="Company name"
//                 />
//               </div>
//             </div>
//           )}
          
//           {step === 7 && (
//             <div>
//               <p className="text-gray-600 mb-4">
//                 Add your shipping address (optional - you can add this later)
//               </p>
//               <div className="mb-4">
//                 <label className="block text-gray-700 text-sm font-medium mb-2">
//                   Street Address
//                 </label>
//                 <input
//                   type="text"
//                   value={street}
//                   onChange={(e) => setStreet(e.target.value)}
//                   className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   placeholder="Street address"
//                 />
//               </div>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-gray-700 text-sm font-medium mb-2">
//                     City
//                   </label>
//                   <input
//                     type="text"
//                     value={city}
//                     onChange={(e) => setCity(e.target.value)}
//                     className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     placeholder="City"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-gray-700 text-sm font-medium mb-2">
//                     Province
//                   </label>
//                   <input
//                     type="text"
//                     value={state}
//                     onChange={(e) => setState(e.target.value)}
//                     className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     placeholder="Province"
//                   />
//                 </div>
//               </div>
//               <div className="mt-4">
//                 <label className="block text-gray-700 text-sm font-medium mb-2">
//                   Postal Code
//                 </label>
//                 <input
//                   type="text"
//                   value={postalCode}
//                   onChange={(e) => setPostalCode(e.target.value)}
//                   className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   placeholder="Postal code"
//                 />
//               </div>
//             </div>
//           )}
          
//           <div className="mt-6 flex flex-col sm:flex-row-reverse gap-2">
//             <button
//               type="submit"
//               className="w-full sm:w-auto bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 text-sm font-medium disabled:opacity-50"
//               disabled={loading}
//             >
//               {loading ? 'Processing...' : step === 1 ? 'Continue' : 
//                         step === 2 ? 'Sign In' : 
//                         step === 3 ? 'Verify' : 
//                         step === 7 ? 'Complete Registration' : 'Continue'}
//             </button>
            
//             {step > 1 && (
//               <button
//                 type="button"
//                 onClick={() => setStep(step - 1)}
//                 className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
//               >
//                 Back
//               </button>
//             )}
//           </div>
//         </form>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default CartSignInUp;
